import React from 'react';
import { SpeakerStats } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';
import { ProgressBar, toneFillClass, type ProgressTone } from '../../ui/ProgressBar';

interface TalkPercentSummaryCardProps {
  speakerStats?: SpeakerStats;
}

const TalkPercentSummaryCard: React.FC<TalkPercentSummaryCardProps> = ({
  speakerStats,
}) => {
  if (!speakerStats) {
    return (
      <SummarySectionCard
        title="Talk %"
        resultContent={null}
        missingDataMessage="Speaker stats not available for this session."
      />
    );
  }

  const repPct = Math.round(speakerStats.rep_pct ?? 0);
  const customerPct = Math.round(speakerStats.customer_pct ?? 0);

  const tone: ProgressTone = repPct > 65 ? 'error' : repPct > 50 ? 'warning' : 'success';

  const badgeColor =
    repPct > 65
      ? 'bg-error-tint text-error border border-error'
      : repPct > 50
      ? 'bg-warning-tint text-warning border border-warning'
      : 'bg-success-tint text-success border border-success';

  const statusBadge = (
    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${badgeColor}`}>Rep: {repPct}%</span>
  );

  const coachingText =
    repPct > 65
      ? `Talk time was high at ${repPct}%. Aim to stay under 50% to give the customer more space to share their needs and concerns.`
      : repPct > 50
      ? `Talk ratio of ${repPct}% is slightly above ideal. Asking more open-ended questions can help balance the conversation.`
      : `Good balance at ${repPct}% talk time. Keep encouraging the customer to share and stay curious.`;

  const resultContent = (
    <div className="space-y-2">
      <div className="flex text-xs text-ink-muted justify-between mb-1">
        <span>Rep ({repPct}%)</span>
        <span>Customer ({customerPct}%)</span>
      </div>
      {/* The rep share is the fill and the customer share is the rest of the track. */}
      <ProgressBar value={repPct} tone={tone} />
      <div className="flex items-center gap-3 text-[10px] text-ink-muted mt-1">
        <span className="flex items-center gap-1"><span className={`inline-block w-2 h-2 rounded-full ${toneFillClass(tone)}`} /> Rep</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-surface-sunken" /> Customer</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2 text-center">
        <div className="bg-surface-subtle rounded p-2">
          <p className="text-xs font-bold text-ink-secondary">{speakerStats.rep_word_count?.toLocaleString() ?? '—'}</p>
          <p className="text-[10px] text-ink-muted">Rep words</p>
        </div>
        <div className="bg-surface-subtle rounded p-2">
          <p className="text-xs font-bold text-ink-secondary">{speakerStats.customer_word_count?.toLocaleString() ?? '—'}</p>
          <p className="text-[10px] text-ink-muted">Customer words</p>
        </div>
      </div>
    </div>
  );

  return (
    <SummarySectionCard
      title="Talk %"
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={[coachingText]}
      sectionKey="talk_pct"
    />
  );
};

export default TalkPercentSummaryCard;
